import { useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/context/AuthContext';

const RootRedirect = () => {
    const { hasPrivileges, viewAs, rolesLoading } = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();

    const performRedirection = useCallback(() => {
        const pushLog = (message, payload) => {
            try {
                const entry = { ts: new Date().toISOString(), message, payload };
                // in-memory buffer
                if (!window.__appRedirectLogs) window.__appRedirectLogs = [];
                window.__appRedirectLogs.push(entry);
                // keep only last 200
                if (window.__appRedirectLogs.length > 200) window.__appRedirectLogs.shift();
                // persist to localStorage for retrieval
                try { localStorage.setItem('redirectLogs', JSON.stringify(window.__appRedirectLogs)); } catch (e) { /* ignore */ }
            } catch (e) {
                // ignore
            }
        };

        // Attendre que les privilèges soient chargés pour les utilisateurs authentifiés
        if (rolesLoading) {
            pushLog('privileges-loading');
            return;
        }

        // Ne rien faire si on n'est pas sur la route racine "/"
        if (location.pathname !== '/') {
            pushLog('skip-not-root', { path: location.pathname });
            return;
        }

        const debugPayload = {
            pathname: location.pathname,
            viewAs: viewAs,
            rolesLoading: rolesLoading
        };
        pushLog('performing-redirection', debugPayload);

            // Définir les chemins de redirection en fonction des rôles (pour faciliter la maintenance)
            const rolePaths = {
                "USER_": "/administration",
                "ADMIN_": "/consultant"
            };

            // Vérifier les privilèges disponibles
            const hasAdminPrivileges = hasPrivileges("ADMIN_");
            const hasUserPrivileges = hasPrivileges("USER_");

            // Logique de redirection basée sur viewAs et les droits disponibles
            if (hasPrivileges(viewAs)) {
                // L'utilisateur a les privilèges pour son viewAs actuel
                pushLog('redirect', { to: rolePaths[viewAs], role: viewAs, reason: 'current-viewas-has-privileges' });
                navigate(rolePaths[viewAs], { replace: true });
            } else if (hasAdminPrivileges && hasUserPrivileges) {
                // L'utilisateur a les deux types de privilèges, on privilégie ADMIN
                pushLog('redirect', { to: rolePaths["ADMIN_"], role: 'ADMIN_', reason: 'both-privileges-default-admin' });
                navigate(rolePaths["ADMIN_"], { replace: true });
            } else if (hasAdminPrivileges) {
                // Utilisateur avec droits admin uniquement
                pushLog('redirect', { to: rolePaths["ADMIN_"], role: 'ADMIN_', reason: 'admin-only' });
                navigate(rolePaths["ADMIN_"], { replace: true });
            } else if (hasUserPrivileges) {
                // Utilisateur avec droits user uniquement
                pushLog('redirect', { to: rolePaths["USER_"], role: 'USER_', reason: 'user-only' });
                navigate(rolePaths["USER_"], { replace: true });
            } else {
                pushLog('waiting-privileges', { reason: 'no-privileges-yet' });
                return;
            }

    }, [hasPrivileges, viewAs, navigate, location.pathname, rolesLoading]);

    useEffect(() => {
        performRedirection();
    }, [performRedirection]);

    return null;
};

export default RootRedirect;
